import Footer from "@components/Footer"
import Image from "@components/Image"
import { useIntl } from "@components/Intl"
import { Box, Container } from "@components/Layout"
import SEO from "@components/SEO"
import { OpeningTimes, SingleStore } from "@components/SingleStore"
import { CityTitle, Paragraph, TitleXL } from "@components/Typography"
import { getKontentImage, getPageByCodename } from "@lib/kontent"
import { RetailersPage } from "@lib/kontent/models-deprecated/retailers_page"
import { RetailersRegionBlock } from "@lib/kontent/models-deprecated/retailers___region_block"
import { RetailersRetailerBlock } from "@lib/kontent/models-deprecated/retailers___retailer_block"
import { PageProps } from "@lib/types"
import { ErrorPage } from "@screens/error"
import { NextPage } from "next"

type Props = PageProps<{ content: RetailersPage }>

const dayKeys = [
  "monday",
  "tuesday",
  "wednseday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]

const Return: NextPage<Props> = ({ statusCode, data }) => {
  const { messages } = useIntl()
  if (statusCode || !data) {
    return <ErrorPage statusCode={statusCode} />
  }

  return (
    <>
      <SEO
        title={data.content.seoMetaTitle.value}
        description={data.content.seoMetaDescription.value}
        imageUrl={data.content.seoMetaImage.value[0]?.url}
        urlKey="retailers"
      />
      <Box pt={["6rem"]}>
        {data.content.pageIntroHero.value[0] && (
          <Box position="relative">
            <Image
              variant="full-width"
              src={getKontentImage(data.content.pageIntroHero.value[0].url, {
                width: 1200,
              })}
              alt="background"
            />
          </Box>
        )}
        <Container pt={["5rem"]} pb={"6rem"} maxWidth="75rem">
          <TitleXL as="h2">{data.content.pageIntroTitle.value}</TitleXL>
          {data.content.pageIntroText.value && (
            <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: "1rem" }}>
              {data.content.pageIntroText.value}
            </Paragraph>
          )}
          <Box
            display="grid"
            gridTemplateColumns={["1fr", "1fr 1fr", "1fr 1fr 1fr"]}
            gridGap="3"
            pt={["0", "4"]}
          >
            {data.content.retailers.value.map((item) => {
              if (item.system.type === "retailers___region_block") {
                const region = item as RetailersRegionBlock
                return (
                  <Box gridColumn={["1", "1 / span 3"]} key={region.system.id}>
                    <CityTitle>{region.title.value}</CityTitle>
                  </Box>
                )
              }
              if (item.system.type === "retailers___retailer_block") {
                const retailer = item as RetailersRetailerBlock
                const openingHours: OpeningTimes[] = []
                dayKeys.forEach((key) => {
                  if (retailer[key] && retailer[key].value.length) {
                    openingHours.push({
                      day: messages.retailers[key],
                      hours: retailer[key].value,
                    })
                  }
                })

                return (
                  <SingleStore
                    title={retailer.title.value}
                    subtitle={retailer.subtitle.value}
                    tel={retailer.phoneNumber.value}
                    mail={retailer.emailAddress.value}
                    url={retailer.websiteUrl.value}
                    openingHours={openingHours}
                    key={retailer.system.id}
                  >
                    <Paragraph
                      style={{ display: "block", whiteSpace: "pre-wrap" }}
                    >
                      {retailer.addressInfo.value}
                    </Paragraph>
                  </SingleStore>
                )
              }
              return null
            })}
          </Box>
        </Container>
      </Box>
      <Footer />
    </>
  )
}

Return.getInitialProps = async ({ lang }) => {
  try {
    const content = await getPageByCodename({ codename: "retailers", lang })
    if (!content) {
      return { statusCode: 404 }
    }
    return {
      data: { content: content.item as RetailersPage },
    }
  } catch (error) {
    return { statusCode: 500 }
  }
}

export default Return
